/**
 * BoardSettings - Full-page settings for Board Governance
 * Includes: Executive Committee, Board Members, Quorum, Permissions, Meeting Dashboard (Quick Links)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Users, Settings, Shield, LayoutDashboard, Save, Loader2,
  Plus, X, FileText, FolderOpen, Link2, StickyNote, GripVertical, Trash2, Pencil,
  Bell, Lock, UserCheck, CalendarCheck, Building2
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import RichTextEditor from './RichTextEditor';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import { getCommitteeColor } from '../utils/committeeColors';
import {
  DEFAULT_MEETING_NOTIFICATION_CADENCE,
  MEETING_REMINDER_OPTIONS,
  describeReminderMinutes,
  normalizeReminderCadence,
} from '../utils/meetingReminderCadence';

// Position labels mapping
const POSITION_LABELS = {
  chair: 'Chair',
  vice_chair: 'Vice Chair',
  secretary: 'Secretary',
  treasurer: 'Treasurer',
  president: 'President',
  executive_director: 'Executive Director',
  member_at_large: 'Member at Large'
};

// Icon options for dashboard items
const ICON_OPTIONS = [
  { value: 'FileText', label: 'Document', Icon: FileText },
  { value: 'FolderOpen', label: 'Folder', Icon: FolderOpen },
  { value: 'Link2', label: 'Link', Icon: Link2 },
  { value: 'StickyNote', label: 'Note', Icon: StickyNote },
];

// Item types
const ITEM_TYPES = [
  { value: 'note', label: 'Note', description: 'Add a quick note or reminder' },
  { value: 'link', label: 'Link', description: 'Link to a page or external URL' },
  { value: 'document', label: 'Document', description: 'Link to a board document' },
  { value: 'divider', label: 'Section Divider', description: 'Section divider with label' },
];

const DEFAULT_BOARD_MEETING_NOTIFICATION_SUBJECT = 'Meeting Reminder: {{meeting.title}}';

// Icon renderer helper
const IconRenderer = ({ name, className }) => {
  switch (name) {
    case 'FolderOpen':
      return <FolderOpen className={className} />;
    case 'Link2':
      return <Link2 className={className} />;
    case 'StickyNote':
      return <StickyNote className={className} />;
    case 'FileText':
    default:
      return <FileText className={className} />;
  }
};

// =============================================================================
// DASHBOARD ITEMS EDITOR COMPONENT
// =============================================================================

const DashboardItemEditor = ({ item, onSave, onCancel, documents = [] }) => {
  const [formData, setFormData] = useState({
    type: item?.type || 'link',
    title: item?.title || '',
    content: item?.content || '',
    url: item?.url || '',
    document_id: item?.document_id || '',
    icon: item?.icon || 'FileText',
  });
  const [saving, setSaving] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (formData.type === 'link' && !formData.url.trim()) {
      toast.error('URL is required for link items');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...formData,
        id: item?.id,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="grid grid-cols-2 gap-4">
        {/* Type Selection */}
        <div>
          <Label className="text-sm font-medium">Item Type</Label>
          <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ITEM_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Icon Selection */}
        <div>
          <Label className="text-sm font-medium">Icon</Label>
          <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ICON_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  <div className="flex items-center gap-2">
                    <opt.Icon className="h-4 w-4" />
                    <span>{opt.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Title */}
      <div>
        <Label className="text-sm font-medium">Title</Label>
        <Input
          className="mt-1"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Enter title..."
          required
        />
      </div>

      {/* Type-specific fields */}
      {formData.type === 'note' && (
        <div>
          <Label className="text-sm font-medium">Note Content</Label>
          <div className="mt-1 border border-gray-200 rounded-md bg-white">
            <RichTextEditor
              value={formData.content}
              onChange={(value) => setFormData({ ...formData, content: value })}
              placeholder="Enter note content..."
              className="min-h-[140px]"
              enableMentions={false}
              enableVariables={false}
            />
          </div>
        </div>
      )}

      {formData.type === 'link' && (
        <div>
          <Label className="text-sm font-medium">URL</Label>
          <Input
            className="mt-1"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            placeholder="/governance/documents or https://..."
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Use relative paths for internal pages, or full URLs for external links
          </p>
        </div>
      )}

      {formData.type === 'document' && (
        <div>
          <Label className="text-sm font-medium">Document</Label>
          <Select 
            value={formData.document_id} 
            onValueChange={(v) => setFormData({ ...formData, document_id: v })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select a document..." />
            </SelectTrigger>
            <SelectContent>
              <div className="p-2">
                <Input
                  value={documentSearch}
                  onChange={(e) => setDocumentSearch(e.target.value)}
                  placeholder="Search documents..."
                  className="h-8"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              {(documents.filter(doc => doc.name.toLowerCase().includes(documentSearch.toLowerCase()))).length === 0 ? (
                <div className="px-2 py-4 text-sm text-gray-500 text-center">
                  No documents available
                </div>
              ) : (
                documents
                  .filter(doc => doc.name.toLowerCase().includes(documentSearch.toLowerCase()))
                  .map(doc => (
                  <SelectItem key={doc.id} value={doc.id}>
                    {doc.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {item?.id ? 'Update' : 'Add'}
        </Button>
      </div>
    </form>
  );
};

// =============================================================================
// DASHBOARD ITEM ROW COMPONENT
// =============================================================================

const DashboardItemRow = ({ item, onEdit, onDelete, onDragStart, onDragOver, onDrop }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, item.id)}
      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 group hover:border-purple-300 cursor-move transition-colors"
    >
      <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
      <IconRenderer name={item.icon} className="h-4 w-4 text-purple-600" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
        <p className="text-xs text-gray-500 capitalize">{item.type}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(item)}>
          <Pencil className="h-3.5 w-3.5 text-gray-500" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-red-500 hover:text-red-600" 
          onClick={() => onDelete(item.id)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN BOARD SETTINGS COMPONENT
// =============================================================================

const BoardSettings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'executive';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState(null);
  const [governanceSettings, setGovernanceSettings] = useState({ committees_enabled: false });
  const [executiveCommittee, setExecutiveCommittee] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedBoardMember, setSelectedBoardMember] = useState('');
  const [selectedBoardPosition, setSelectedBoardPosition] = useState('none');
  const [addingBoardMember, setAddingBoardMember] = useState(false);
  const [updatingVoting, setUpdatingVoting] = useState({});

  // Committees state
  const [committees, setCommittees] = useState([]);
  const [committeesLoading, setCommitteesLoading] = useState(false);
  const [committeesSaving, setCommitteesSaving] = useState(false);
  const [showCreateCommittee, setShowCreateCommittee] = useState(false);
  const [newCommittee, setNewCommittee] = useState({ name: '', description: '', color_hex: '#7C3AED', chair_user_id: '' });
  
  // Dashboard settings state
  const [dashboardSettings, setDashboardSettings] = useState(null);
  const [meetingReminderCadence, setMeetingReminderCadence] = useState(DEFAULT_MEETING_NOTIFICATION_CADENCE);
  const [dashboardItems, setDashboardItems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [draggedId, setDraggedId] = useState(null);

  const FOLLOWUP_PERMISSION_OPTIONS = [
    { value: 'chair', label: 'Chair' },
    { value: 'vice_chair', label: 'Vice Chair' },
    { value: 'secretary', label: 'Secretary' },
    { value: 'treasurer', label: 'Treasurer' },
    { value: 'member', label: 'Board Member' },
    { value: 'executive_committee', label: 'Executive Committee' },
    { value: 'admin', label: 'Admins' },
  ];

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchCommittees = async () => {
    setCommitteesLoading(true);
    try {
      const response = await api.get('/governance/access');
      setCommittees(response.data?.committees || []);
    } catch (err) {
      console.error('Failed to fetch committees:', err);
      setCommittees([]);
    } finally {
      setCommitteesLoading(false);
    }
  };

  const fetchBoardMembers = async () => {
    try {
      const membersRes = await api.get('/board/documents/members');
      setBoardMembers(membersRes.data.members || []);
    } catch (err) {
      console.error('Failed to fetch board members:', err);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch each data source separately to avoid one failure blocking others
      const [settingsRes, membersRes, dashboardRes, docsRes, projectsRes, governanceRes, teamRes] = await Promise.allSettled([
        api.get('/board/documents/settings'),
        api.get('/board/documents/members'),
        api.get('/board/dashboard-settings/default'),
        api.get('/board/documents?include_all=true&limit=200&offset=0'),
        api.get('/projects?client_id=null'),
        api.get('/governance/settings'),
        api.get('/team'),
      ]);
      
      if (settingsRes.status === 'fulfilled') {
        setSettings(settingsRes.value.data.settings);
        setMeetingReminderCadence(normalizeReminderCadence(settingsRes.value.data.settings?.meeting_notification_reminder_minutes || DEFAULT_MEETING_NOTIFICATION_CADENCE));
        setExecutiveCommittee(settingsRes.value.data.executive_committee || []);
      }
      
      if (membersRes.status === 'fulfilled') {
        setBoardMembers(membersRes.value.data.members || []);
      }
      
      if (dashboardRes.status === 'fulfilled') {
        setDashboardSettings(dashboardRes.value.data);
        setDashboardItems(dashboardRes.value.data.items || []);
      }
      
      if (docsRes.status === 'fulfilled') {
        const data = docsRes.value.data || {};
        setDocuments(data.documents || data.items || []);
      }

      if (projectsRes.status === 'fulfilled') {
        setProjects(projectsRes.value.data?.projects || projectsRes.value.data || []);
      }

      if (teamRes.status === 'fulfilled') {
        setTeamMembers(teamRes.value.data?.members || []);
      }

      if (governanceRes.status === 'fulfilled') {
        setGovernanceSettings(governanceRes.value.data || { committees_enabled: false });
        if (governanceRes.value.data?.committees_enabled) {
          await fetchCommittees();
        } else {
          setCommittees([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Save board settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const settingsToSave = {
        max_file_size_mb: settings?.max_file_size_mb,
        allowed_file_types: settings?.allowed_file_types,
        notify_on_new_document: settings?.notify_on_new_document,
        notify_on_new_comment: settings?.notify_on_new_comment,
        notify_on_mention: settings?.notify_on_mention,
        default_document_access: settings?.default_document_access,
        quorum_type: settings?.quorum_type,
        quorum_value: settings?.quorum_value,
        quorum_count_only_voting: settings?.quorum_count_only_voting,
        meeting_start_roles: settings?.meeting_start_roles,
        meeting_executive_can_start: settings?.meeting_executive_can_start,
        agenda_edit_roles: settings?.agenda_edit_roles,
        agenda_edit_allow_executive: settings?.agenda_edit_allow_executive,
        agenda_edit_allow_admins: settings?.agenda_edit_allow_admins,
        followup_project_id: settings?.followup_project_id,
        meeting_followup_generation_permission: settings?.meeting_followup_generation_permission ?? settings?.followup_task_roles,
        followup_task_allow_executive: settings?.followup_task_allow_executive,
        followup_task_allow_admins: settings?.followup_task_allow_admins,
        meeting_notifications_enabled: settings?.meeting_notifications_enabled,
        meeting_notification_subject: settings?.meeting_notification_subject,
        meeting_notification_reminder_minutes: normalizeReminderCadence(meetingReminderCadence)
      };
      await api.put('/board/documents/settings', settingsToSave);
      toast.success('Settings saved');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };


  const handleToggleCommittees = async (nextValue) => {
    setCommitteesSaving(true);
    try {
      await api.put('/governance/settings', { committees_enabled: nextValue });
      setGovernanceSettings(prev => ({ ...prev, committees_enabled: nextValue }));
      if (nextValue) {
        await fetchCommittees();
        toast.success('Committees enabled');
      } else {
        setCommittees([]);
        setShowCreateCommittee(false);
        toast.success('Committees disabled');
      }
    } catch (err) {
      toast.error('Failed to update committee settings');
    } finally {
      setCommitteesSaving(false);
    }
  };

  const handleCreateCommittee = async () => {
    if (!newCommittee.name.trim()) {
      toast.error('Committee name is required');
      return;
    }
    setCommitteesSaving(true);
    try {
      const response = await api.post('/governance/committees', {
        name: newCommittee.name.trim(),
        description: newCommittee.description.trim() || null,
        color_hex: newCommittee.color_hex || null,
        chair_user_id: newCommittee.chair_user_id || null
      });
      setCommittees(prev => [...prev, response.data?.committee].filter(Boolean));
      setNewCommittee({ name: '', description: '', color_hex: '#7C3AED', chair_user_id: '' });
      setShowCreateCommittee(false);
      toast.success('Committee created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create committee');
    } finally {
      setCommitteesSaving(false);
    }
  };

  const handleCommitteeColorSave = async (committeeId, colorHex) => {
    if (!colorHex) return;
    setCommitteesSaving(true);
    try {
      await api.put(`/governance/committees/${committeeId}`, { color_hex: colorHex });
      setCommittees(prev => prev.map(c => c.id === committeeId ? { ...c, color_hex: colorHex } : c));
      toast.success('Committee color updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update committee color');
    } finally {
      setCommitteesSaving(false);
    }
  };

  const handleCommitteeColorChange = async (committeeId, colorHex) => {
    if (!colorHex) return;
    setCommittees(prev => prev.map(c => c.id === committeeId ? { ...c, color_hex: colorHex } : c));
    await handleCommitteeColorSave(committeeId, colorHex);
  };

  const availableBoardMembers = teamMembers.filter((member) => {
    const memberId = member.user?.id || member.id;
    return !boardMembers.some((bm) => bm.id === memberId);
  });

  // Executive Committee handlers
  const handleAddExecutiveMember = async () => {
    if (!selectedMember) return;
    try {
      await api.post('/board/documents/settings/executive-committee', {
        user_id: selectedMember,
        position: selectedPosition || null
      });
      setSelectedMember('');
      setSelectedPosition('');
      fetchAllData();
      toast.success('Member added to Executive Committee');
    } catch (err) {
      toast.error('Failed to add member');
    }
  };

  const handleAddBoardMember = async () => {
    if (!selectedBoardMember) return;
    setAddingBoardMember(true);
    try {
      await api.put(`/board/documents/members/${selectedBoardMember}/position`, {
        position: selectedBoardPosition === 'none' ? null : selectedBoardPosition
      });
      await fetchBoardMembers();
      setSelectedBoardMember('');
      setSelectedBoardPosition('none');
      toast.success('Board member added');
    } catch (err) {
      console.error('Failed to add board member:', err);
      toast.error('Failed to add board member');
    } finally {
      setAddingBoardMember(false);
    }
  };

  const handleRemoveExecutiveMember = async (userId) => {
    try {
      await api.delete(`/board/documents/settings/executive-committee/${userId}`);
      fetchAllData();
      toast.success('Member removed from Executive Committee');
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  // Voting status handler
  const handleToggleVotingStatus = async (memberId, currentStatus) => {
    setUpdatingVoting(prev => ({ ...prev, [memberId]: true }));
    try {
      await api.put(`/board/documents/members/${memberId}/voting-status`, {
        is_voting_member: !currentStatus
      });
      setBoardMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, is_voting_member: !currentStatus } : m
      ));
      toast.success(`Member marked as ${!currentStatus ? 'voting' : 'non-voting'}`);
    } catch (err) {
      toast.error('Failed to update voting status');
    } finally {
      setUpdatingVoting(prev => ({ ...prev, [memberId]: false }));
    }
  };

  // Dashboard item handlers
  const handleSaveDashboardItem = async (itemData) => {
    if (!dashboardSettings?.id) return;

    try {
      if (itemData.id) {
        await api.put(`/board/dashboard-settings/${dashboardSettings.id}/items/${itemData.id}`, itemData);
        setDashboardItems(prev => prev.map(i => i.id === itemData.id ? { ...i, ...itemData } : i));
        toast.success('Item updated');
      } else {
        const response = await api.post(`/board/dashboard-settings/${dashboardSettings.id}/items`, {
          ...itemData,
          order: dashboardItems.length,
        });
        setDashboardItems(prev => [...prev, response.data.item]);
        toast.success('Item added');
      }
      setEditingItem(null);
      setIsAddingItem(false);
    } catch (err) {
      toast.error('Failed to save item');
    }
  };

  const handleDeleteDashboardItem = async (itemId) => {
    if (!dashboardSettings?.id) return;
    if (!window.confirm('Delete this item?')) return;

    try {
      await api.delete(`/board/dashboard-settings/${dashboardSettings.id}/items/${itemId}`);
      setDashboardItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Item deleted');
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  // Drag and drop
  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId || !dashboardSettings?.id) return;

    const draggedIndex = dashboardItems.findIndex(i => i.id === draggedId);
    const targetIndex = dashboardItems.findIndex(i => i.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newItems = [...dashboardItems];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    const reorderedItems = newItems.map((item, index) => ({ ...item, order: index }));
    setDashboardItems(reorderedItems);
    setDraggedId(null);

    try {
      await api.put(`/board/dashboard-settings/${dashboardSettings.id}/items/reorder`, {
        item_ids: reorderedItems.map(i => i.id),
      });
    } catch (err) {
      console.error('Failed to reorder items:', err);
    }
  };

  const availableMembers = boardMembers.filter(
    m => !executiveCommittee.some(ec => ec.user_id === m.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="board-settings-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/governance')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Governance
              </Button>
              <div className="border-l border-gray-200 h-6" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Settings className="h-5 w-5 text-purple-600" />
                  Board Settings
                </h1>
                <p className="text-sm text-gray-500">Configure your board governance settings</p>
              </div>
            </div>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1">
            <TabsTrigger value="executive" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Executive Committee
            </TabsTrigger>
            <TabsTrigger value="members" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Board Members
            </TabsTrigger>
            <TabsTrigger value="committees" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Committees
            </TabsTrigger>
            <TabsTrigger value="quorum" className="flex items-center gap-2">
              <CalendarCheck className="h-4 w-4" />
              Quorum & Meetings
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Meeting Dashboard
            </TabsTrigger>
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Permissions
            </TabsTrigger>
          </TabsList>

          {/* Executive Committee Tab */}
          <TabsContent value="executive" className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Executive Committee</h3>
              <p className="text-sm text-gray-500 mb-6">
                Executive committee members have access to locked documents and folders, and may have additional meeting permissions.
              </p>
              
              {/* Current Members */}
              <div className="space-y-2 mb-6">
                {executiveCommittee.length === 0 ? (
                  <p className="text-gray-400 text-sm italic py-4 text-center">No executive committee members assigned</p>
                ) : (
                  executiveCommittee.map((member) => (
                    <div key={member.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-sm font-medium text-purple-600">
                          {member.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          {member.position && (
                            <p className="text-xs text-purple-600 font-medium">{POSITION_LABELS[member.position] || member.position}</p>
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveExecutiveMember(member.user_id)}
                      >
                        <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
              
              {/* Add Member */}
              <div className="flex gap-2">
                <Select value={selectedMember} onValueChange={setSelectedMember}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select board member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Position..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(POSITION_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleAddExecutiveMember} disabled={!selectedMember}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Board Members Tab */}
          <TabsContent value="members" className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Board Members</h3>
              <p className="text-sm text-gray-500 mb-6">
                Assign positions and toggle voting status for board members. Non-voting members will not count toward quorum if &quot;Count only voting members&quot; is enabled.
              </p>

              <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-2 mb-6">
                <div className="flex-1">
                  <Label>Add Board Member</Label>
                  <Select value={selectedBoardMember} onValueChange={setSelectedBoardMember}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBoardMembers.map((member) => (
                        <SelectItem key={member.user?.id || member.id} value={member.user?.id || member.id}>
                          {member.user?.name || member.name || member.user?.email || member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full md:w-48">
                  <Label>Role</Label>
                  <Select value={selectedBoardPosition} onValueChange={setSelectedBoardPosition}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Role..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No position</SelectItem>
                      {Object.entries(POSITION_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddBoardMember} disabled={!selectedBoardMember || addingBoardMember}>
                  {addingBoardMember ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Add
                </Button>
              </div>

              <div className="space-y-2">
                {boardMembers.length === 0 ? (
                  <p className="text-gray-400 text-sm italic py-4 text-center">No board members found</p>
                ) : (
                  boardMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-sm font-medium text-purple-600">
                          {member.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <Select
                            value={member.position || 'none'}
                            onValueChange={async (value) => {
                              try {
                                await api.put(`/board/documents/members/${member.id}/position`, {
                                  position: value === 'none' ? null : value
                                });
                                setBoardMembers(prev => prev.map(m => 
                                  m.id === member.id ? { ...m, position: value === 'none' ? null : value } : m
                                ));
                                toast.success('Position updated');
                              } catch (err) {
                                toast.error('Failed to update position');
                              }
                            }}
                          >
                            <SelectTrigger className="h-7 w-40 text-xs mt-1">
                              <SelectValue>
                                {member.position ? POSITION_LABELS[member.position] || member.position : 'No position'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No position</SelectItem>
                              {Object.entries(POSITION_LABELS).map(([value, label]) => (
                                <SelectItem key={value} value={value}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={member.is_voting_member ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                          {member.is_voting_member ? 'Voting' : 'Non-Voting'}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleToggleVotingStatus(member.id, member.is_voting_member)}
                          disabled={updatingVoting[member.id]}
                        >
                          {updatingVoting[member.id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            member.is_voting_member ? 'Make Non-Voting' : 'Make Voting'
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Committees Tab */}
          <TabsContent value="committees" className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Committees</h3>
              <p className="text-sm text-gray-500 mb-6">
                Enable committee management and configure committee lists for governance.
              </p>

              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Enable committees</p>
                  <p className="text-sm text-gray-500">Allow teams to create and manage committees</p>
                </div>
                <Switch
                  checked={governanceSettings?.committees_enabled || false}
                  onCheckedChange={handleToggleCommittees}
                  disabled={committeesSaving}
                />
              </div>
            </div>

            {governanceSettings?.committees_enabled && (
              <>
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Committee List</h3>
                    <Button onClick={() => setShowCreateCommittee(true)} disabled={committeesSaving}>
                      <Plus className="h-4 w-4 mr-2" />
                      New Committee
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">
                    Committees determine access to committee workspaces and committee-specific governance views.
                  </p>

                  {committeesLoading ? (
                    <div className="flex items-center justify-center py-6 text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Loading committees...
                    </div>
                  ) : committees.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Building2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No committees yet</p>
                      <p className="text-xs text-gray-400 mt-1">Create your first committee to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {committees.map((committee) => {
                        const color = getCommitteeColor(committee.name, committee.color_hex);
                        return (
                          <div key={committee.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <div className="flex items-start gap-3">
                              <div
                                className="mt-1 h-3 w-3 rounded-full border border-white shadow-sm"
                                style={{ background: color.accent }}
                              />
                              <div>
                                <p className="font-medium text-gray-900">{committee.name}</p>
                                {committee.description && (
                                  <p className="text-xs text-gray-500 mt-1">{committee.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={committee.color_hex || '#7C3AED'}
                                  onChange={(e) => handleCommitteeColorChange(committee.id, e.target.value)}
                                  className="h-7 w-7 rounded border border-gray-200 bg-white"
                                  aria-label="Committee color"
                                />
                                <Input
                                  value={committee.color_hex || '#7C3AED'}
                                  onChange={(e) => {
                                    const next = e.target.value.toUpperCase();
                                    setCommittees(prev => prev.map(c => c.id === committee.id ? { ...c, color_hex: next } : c));
                                  }}
                                  onBlur={(e) => handleCommitteeColorSave(committee.id, e.target.value)}
                                  className="h-7 w-24 text-xs"
                                />
                              </div>
                              <div className="text-xs text-gray-500">
                                {committee.member_count ?? 0} members
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {showCreateCommittee && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Committee</h3>
                    <p className="text-sm text-gray-500 mb-6">
                      Create a committee to organize governance access and meeting workflows.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <Label>Committee Name</Label>
                        <Input
                          className="mt-1"
                          value={newCommittee.name}
                          onChange={(e) => setNewCommittee({ ...newCommittee, name: e.target.value })}
                          placeholder="e.g. Audit Committee"
                        />
                      </div>
                      <div>
                        <Label>Committee Chair</Label>
                        <Select
                          value={newCommittee.chair_user_id}
                          onValueChange={(value) => setNewCommittee({ ...newCommittee, chair_user_id: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select a chair..." />
                          </SelectTrigger>
                          <SelectContent>
                            {teamMembers.map((member) => (
                              <SelectItem key={member.user?.id || member.id} value={member.user?.id || member.id}>
                                {member.user?.name || member.name || member.user?.email || member.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Primary Color</Label>
                        <div className="mt-1 flex items-center gap-3">
                          <input
                            type="color"
                            value={newCommittee.color_hex || '#7C3AED'}
                            onChange={(e) => setNewCommittee({ ...newCommittee, color_hex: e.target.value })}
                            className="h-9 w-12 rounded border border-gray-200 bg-white"
                            aria-label="Committee color"
                          />
                          <Input
                            value={newCommittee.color_hex || '#7C3AED'}
                            onChange={(e) => setNewCommittee({ ...newCommittee, color_hex: e.target.value.toUpperCase() })}
                            className="w-32"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          className="mt-1"
                          value={newCommittee.description}
                          onChange={(e) => setNewCommittee({ ...newCommittee, description: e.target.value })}
                          placeholder="Optional description"
                          rows={3}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowCreateCommittee(false);
                          setNewCommittee({ name: '', description: '', color_hex: '#7C3AED', chair_user_id: '' });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleCreateCommittee} disabled={committeesSaving}>
                        {committeesSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                        Create Committee
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Quorum & Meetings Tab */}
          <TabsContent value="quorum" className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quorum Settings</h3>
              <p className="text-sm text-gray-500 mb-6">
                Configure how quorum is calculated for board meetings.
              </p>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Quorum Type</Label>
                    <Select 
                      value={settings?.quorum_type || 'majority'} 
                      onValueChange={(v) => setSettings({...settings, quorum_type: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="majority">Majority (50% + 1)</SelectItem>
                        <SelectItem value="percentage">Custom Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {(settings?.quorum_type === 'percentage' || settings?.quorum_type === 'fixed') && (
                    <div>
                      <Label>{settings?.quorum_type === 'percentage' ? 'Percentage Required' : 'Number Required'}</Label>
                      <Input
                        type="number"
                        className="mt-1"
                        value={settings?.quorum_value || ''}
                        onChange={(e) => setSettings({...settings, quorum_value: parseInt(e.target.value) || 0})}
                        placeholder={settings?.quorum_type === 'percentage' ? '75' : '5'}
                      />
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Count only voting members</p>
                    <p className="text-sm text-gray-500">Exclude non-voting members from quorum calculation</p>
                  </div>
                  <Switch
                    checked={settings?.quorum_count_only_voting || false}
                    onCheckedChange={(checked) => setSettings({...settings, quorum_count_only_voting: checked})}
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Meeting Start Permissions</h3>
              <p className="text-sm text-gray-500 mb-6">
                Configure who can start and control meetings.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-gray-900">Executive Committee can start meetings</p>
                    <p className="text-sm text-gray-500">Allow executive members to start meetings</p>
                  </div>
                  <Switch
                    checked={settings?.meeting_executive_can_start || false}
                    onCheckedChange={(checked) => setSettings({...settings, meeting_executive_can_start: checked})}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Meeting Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Follow-up Task Generation</h3>
              <p className="text-sm text-gray-500 mb-6">
                Choose the project where follow-up tasks should be created when generated from meeting notes.
              </p>

              <div className="max-w-xl">
                <Label>Follow-up Project</Label>
                <Select
                  value={settings?.followup_project_id || ""}
                  onValueChange={(value) =>
                    setSettings({
                      ...settings,
                      followup_project_id: value === "none" ? null : value
                    })
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Meeting Dashboard Quick Links</h3>
                {!isAddingItem && !editingItem && (
                  <Button onClick={() => setIsAddingItem(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Configure the quick links that appear in the left sidebar during meetings. These links help board members quickly access important documents, notes, and resources.
              </p>
              
              {/* Add/Edit Form */}
              {(isAddingItem || editingItem) && (
                <div className="mb-6">
                  <DashboardItemEditor
                    item={editingItem}
                    documents={documents}
                    onSave={handleSaveDashboardItem}
                    onCancel={() => {
                      setIsAddingItem(false);
                      setEditingItem(null);
                    }}
                  />
                </div>
              )}
              
              {/* Items List */}
              {dashboardItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <LayoutDashboard className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No quick links configured yet</p>
                  <p className="text-xs text-gray-400 mt-1">Add links to help board members during meetings</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {dashboardItems
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((item) => (
                      <DashboardItemRow
                        key={item.id}
                        item={item}
                        onEdit={(item) => {
                          setEditingItem(item);
                          setIsAddingItem(false);
                        }}
                        onDelete={handleDeleteDashboardItem}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      />
                    ))}
                </div>
              )}
              
              <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-sm text-purple-800">
                  <strong>Tip:</strong> Drag items to reorder them. The Quick Links sidebar will appear on the left side of the meeting view for all board members.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Agenda Permissions</h3>
              <p className="text-sm text-gray-500 mb-6">
                Configure who can edit meeting agendas.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-gray-900">Executive Committee can edit agendas</p>
                    <p className="text-sm text-gray-500">Allow executive members to modify meeting agendas</p>
                  </div>
                  <Switch
                    checked={settings?.agenda_edit_allow_executive || false}
                    onCheckedChange={(checked) => setSettings({...settings, agenda_edit_allow_executive: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Admins can edit agendas</p>
                    <p className="text-sm text-gray-500">Allow team admins to modify meeting agendas</p>
                  </div>
                  <Switch
                    checked={settings?.agenda_edit_allow_admins || false}
                    onCheckedChange={(checked) => setSettings({...settings, agenda_edit_allow_admins: checked})}
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Follow-up Task Permissions</h3>
              <p className="text-sm text-gray-500 mb-6">
                Configure who can generate follow-up tasks from meeting notes.
              </p>

              <div className="space-y-4">
                <div>
                  <Label>Allowed roles</Label>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {FOLLOWUP_PERMISSION_OPTIONS.map((option) => {
                      const currentRoles = settings?.meeting_followup_generation_permission ?? settings?.followup_task_roles ?? [];
                      const checked = currentRoles.includes(option.value);
                      return (
                        <label key={option.value} className="flex items-center space-x-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...currentRoles, option.value]
                                : currentRoles.filter((role) => role !== option.value);
                              setSettings({ ...settings, meeting_followup_generation_permission: next });
                            }}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Notification Settings</h3>
              <p className="text-sm text-gray-500 mb-6">
                Configure notification preferences for board activities.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-gray-900">Meeting reminders enabled</p>
                    <p className="text-sm text-gray-500">Send board meeting reminders to all board members using this shared configuration.</p>
                  </div>
                  <Switch
                    checked={settings?.meeting_notifications_enabled ?? true}
                    onCheckedChange={(checked) => setSettings({...settings, meeting_notifications_enabled: checked})}
                  />
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <Label>Reminder email subject</Label>
                  <Input
                    className="mt-1 max-w-2xl"
                    value={settings?.meeting_notification_subject ?? DEFAULT_BOARD_MEETING_NOTIFICATION_SUBJECT}
                    onChange={(e) => setSettings({ ...settings, meeting_notification_subject: e.target.value })}
                    placeholder={DEFAULT_BOARD_MEETING_NOTIFICATION_SUBJECT}
                  />
                  <p className="text-xs text-gray-400 mt-1">Available placeholders: {'{{meeting.title}}'}, {'{{meeting.date}}'}, {'{{meeting.time}}'}.</p>
                </div>

                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <div>
                    <Label>Reminder cadence</Label>
                    <p className="text-xs text-gray-400 mt-1">Choose when shared board meeting reminders should go out. Saved internally as minutes before the meeting.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                    {MEETING_REMINDER_OPTIONS.map((option) => {
                      const checked = meetingReminderCadence.includes(option.value);
                      return (
                        <label
                          key={option.value}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${checked ? 'border-purple-300 bg-purple-50 text-purple-900' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setMeetingReminderCadence((prev) => normalizeReminderCadence(
                              e.target.checked
                                ? [...prev, option.value]
                                : prev.filter((value) => value !== option.value)
                            ))}
                            className="h-4 w-4 rounded border-gray-300 text-purple-600"
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {meetingReminderCadence.length > 0 ? meetingReminderCadence.map((minutes) => (
                      <Badge key={minutes} variant="secondary">{describeReminderMinutes(minutes)}</Badge>
                    )) : (
                      <p className="text-xs text-amber-600">No reminders selected. Meetings will save without reminder offsets.</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Notify on new documents</p>
                    <p className="text-sm text-gray-500">Send notifications when new documents are uploaded</p>
                  </div>
                  <Switch
                    checked={settings?.notify_on_new_document || false}
                    onCheckedChange={(checked) => setSettings({...settings, notify_on_new_document: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Notify on comments</p>
                    <p className="text-sm text-gray-500">Send notifications when comments are added to documents</p>
                  </div>
                  <Switch
                    checked={settings?.notify_on_new_comment || false}
                    onCheckedChange={(checked) => setSettings({...settings, notify_on_new_comment: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Notify on mentions</p>
                    <p className="text-sm text-gray-500">Send notifications when you are mentioned in comments</p>
                  </div>
                  <Switch
                    checked={settings?.notify_on_mention || false}
                    onCheckedChange={(checked) => setSettings({...settings, notify_on_mention: checked})}
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BoardSettings;
